'use client';

import { Box, Stack, Typography } from '@mui/material';
import { PartitionSegmentType } from '@repo/api-gw-sdk';
import { useState } from 'react';

import { AddSegmentButton } from './AddSegmentButton';
import { PathChip } from './PathChip';
import { SegmentConfigPanel } from './SegmentConfigPanel';
import { createDefaultSegment, hasPrimaryPartition } from './segmentHelpers';

import type { PathSegmentState } from '../IngestionPipelineWizardState';

interface PathBuilderProps {
  segments: PathSegmentState[];
  separator: string;
  readOnly?: boolean;
  showAddButton?: boolean;
  onChange?: (segments: PathSegmentState[]) => void;
}

export function PathBuilder({
  segments,
  separator,
  readOnly = false,
  showAddButton,
  onChange,
}: PathBuilderProps) {
  const [selectedSegmentId, setSelectedSegmentId] = useState<string | null>(
    null
  );
  const effectiveShowAdd = showAddButton ?? !readOnly;
  const selectedSegment =
    segments.find((s) => s.id === selectedSegmentId) ?? null;

  const primaryExistsForSelected = hasPrimaryPartition(
    segments,
    selectedSegmentId ?? undefined
  );

  const handleChipClick = (id: string) => {
    setSelectedSegmentId((prev) => (prev === id ? null : id));
  };

  const handleChipDelete = (id: string) => {
    if (selectedSegmentId === id) setSelectedSegmentId(null);
    onChange?.(segments.filter((s) => s.id !== id));
  };

  const handleAdd = () => {
    const newSeg = createDefaultSegment({
      segmentPartitionType: hasPrimaryPartition(segments)
        ? PartitionSegmentType.PARTITION_SEGMENT_TYPE_SECONDARY
        : PartitionSegmentType.PARTITION_SEGMENT_TYPE_PRIMARY,
    });
    onChange?.([...segments, newSeg]);
    setSelectedSegmentId(newSeg.id);
  };

  const handleSegmentChange = (updated: PathSegmentState) => {
    onChange?.(segments.map((s) => (s.id === updated.id ? updated : s)));
  };

  const borderColor = 'var(--mui-palette-divider)';

  return (
    <Box sx={{ width: '100%' }}>
      {/* Path row — chips rendered inline */}
      <Stack
        direction='row'
        flexWrap='wrap'
        gap='8px'
        sx={{ py: '8px', minHeight: '48px', alignItems: 'flex-end' }}
      >
        {segments.map((seg, index) => {
          const isSelected = selectedSegmentId === seg.id;
          return (
            <Box
              key={seg.id}
              sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}
            >
              {index > 0 && (
                <Typography
                  variant='body2'
                  sx={{
                    fontFamily: 'monospace',
                    color: 'text.secondary',
                    mb: '0px',
                  }}
                >
                  {separator}
                </Typography>
              )}
              <Box
                sx={{
                  position: 'relative',
                  zIndex: isSelected ? 2 : 1,
                  border: isSelected ? `1px solid ${borderColor}` : 'none',
                  borderBottom: isSelected ? 'none' : undefined,
                  borderRadius: isSelected ? '8px 8px 0 0' : undefined,
                  padding: isSelected ? '6px 8px 15px' : undefined,
                  mb: isSelected ? '-9px' : undefined,
                  bgcolor: 'background.paper',
                }}
              >
                <PathChip
                  segment={seg}
                  isSelected={isSelected}
                  readOnly={readOnly}
                  onClick={() => handleChipClick(seg.id)}
                  onDelete={() => handleChipDelete(seg.id)}
                />
              </Box>
            </Box>
          );
        })}

        {effectiveShowAdd && <AddSegmentButton onClick={handleAdd} />}
      </Stack>

      {/* Config panel — connected to the selected chip */}
      {selectedSegment && (
        <Box
          sx={{
            position: 'relative',
            zIndex: 1,
            border: `1px solid ${borderColor}`,
            borderRadius: '0 0 8px 8px',
            bgcolor: 'background.paper',
          }}
        >
          <SegmentConfigPanel
            segment={selectedSegment}
            primaryExists={primaryExistsForSelected}
            readOnly={readOnly}
            onChange={handleSegmentChange}
          />
        </Box>
      )}
    </Box>
  );
}
